import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/base/ComicCard';
import { comics, categories, faqs } from '@/mocks/comics';

export default function Home() {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [email, setEmail] = useState('');
  const [subscribeMsg, setSubscribeMsg] = useState('');

  const latestComics = [...comics].sort(
    (a, b) => new Date(b.updateTime).getTime() - new Date(a.updateTime).getTime()
  );

  const hotComics = [...comics].sort((a, b) => b.views - a.views).slice(0, 6);

  const filteredComics = selectedCategory === 'all'
    ? latestComics.slice(0, 8)
    : latestComics.filter((c) => c.category === selectedCategory).slice(0, 8);

  const handleSubscribe = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubscribeMsg('订阅成功！您将第一时间收到漫画更新提醒。');
    setEmail('');
    setTimeout(() => setSubscribeMsg(''), 4000);
  };

  const organizationSchema = {
    name: 'UU漫画',
    url: 'https://uumanhua.com',
    description: 'UU漫画提供漫画追更、最新漫画更新及连载漫画同步阅读，追漫必备的更新提醒功能，免费漫画全集随时看。',
    sameAs: ['https://weibo.com/uumanhua', 'https://weixin.qq.com/uumanhua'],
  };

  const websiteSchema = {
    name: 'UU漫画 - 漫画追更平台',
    url: 'https://uumanhua.com',
    description: '漫画追更、最新漫画更新、连载漫画同步阅读平台',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://uumanhua.com/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  const faqSchemaItems = faqs.map((f) => ({
    '@type': 'Question',
    name: f.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: f.answer,
    },
  }));

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify([
            { '@context': 'https://schema.org', '@type': 'Organization', ...organizationSchema },
            { '@context': 'https://schema.org', '@type': 'WebSite', ...websiteSchema },
            { '@context': 'https://schema.org', '@type': 'FAQPage', mainEntity: faqSchemaItems },
          ]),
        }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          {/* Hero Section */}
          <section className="relative w-full h-[520px] md:h-[620px] flex items-center justify-center overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Stylized%20Japanese%20manga%20comic%20panel%20collage%20artistic%20background%20with%20dynamic%20action%20lines%20speed%20lines%20halftone%20dot%20patterns%20warm%20orange%20coral%20and%20amber%20tones%20abstract%20geometric%20composition%20modern%20minimalist%20aesthetic%20clean%20artistic%20illustration%20soft%20gradient%20overlay&width=1800&height=900&seq=hero-bg-comic&orientation=landscape"
              alt="UU漫画 - 漫画追更平台"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/50"></div>
            <div className="relative z-10 w-full px-4 md:px-8 text-center">
              <h1 className="font-heading text-4xl md:text-6xl text-background-50 mb-4 tracking-wide">
                UU漫画
              </h1>
              <p className="text-lg md:text-xl text-background-50/90 max-w-2xl mx-auto leading-relaxed">
                漫画追更必备平台 &mdash; 最新漫画更新、连载漫画同步阅读，免费漫画全集随时看，不再错过任何章节
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-8">
                <Link
                  to="/category/rexue"
                  className="px-8 py-3 bg-primary-500 text-background-50 font-medium rounded-full hover:bg-primary-600 transition-all duration-200 cursor-pointer whitespace-nowrap text-sm"
                >
                  开始追漫
                  <i className="ri-arrow-right-line ml-2"></i>
                </Link>
                <Link
                  to="/about"
                  className="px-8 py-3 bg-background-50/20 backdrop-blur-sm text-background-50 font-medium rounded-full hover:bg-background-50/30 transition-all duration-200 border border-background-50/30 cursor-pointer whitespace-nowrap text-sm"
                >
                  了解更多
                </Link>
              </div>
              <div className="flex items-center justify-center gap-8 mt-10">
                <div className="text-center">
                  <p className="text-2xl md:text-3xl font-bold text-background-50">{comics.length}+</p>
                  <p className="text-xs text-background-50/70 mt-1">热门漫画</p>
                </div>
                <div className="w-px h-10 bg-background-50/30"></div>
                <div className="text-center">
                  <p className="text-2xl md:text-3xl font-bold text-background-50">10+</p>
                  <p className="text-xs text-background-50/70 mt-1">分类齐全</p>
                </div>
                <div className="w-px h-10 bg-background-50/30"></div>
                <div className="text-center">
                  <p className="text-2xl md:text-3xl font-bold text-background-50">1000万+</p>
                  <p className="text-xs text-background-50/70 mt-1">漫画爱好者</p>
                </div>
              </div>
            </div>
          </section>

          {/* Category Tabs + Latest Updates */}
          <section className="w-full px-4 md:px-8 py-14 bg-background-50">
            <div className="max-w-7xl mx-auto">
              <div className="flex flex-col md:flex-row md:items-end justify-between mb-8 gap-4">
                <div>
                  <h2 className="font-heading text-2xl md:text-3xl text-foreground-950">
                    最新漫画更新
                  </h2>
                  <p className="text-sm text-foreground-500 mt-1">
                    追更漫画，掌握最新连载漫画更新动态
                  </p>
                </div>
                <div className="flex items-center gap-1.5 flex-wrap">
                  <button
                    onClick={() => setSelectedCategory('all')}
                    className={`px-4 py-1.5 text-xs font-medium rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer ${
                      selectedCategory === 'all'
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    全部
                  </button>
                  {categories.slice(0, 6).map((cat) => (
                    <button
                      key={cat.slug}
                      onClick={() => setSelectedCategory(cat.slug)}
                      className={`px-4 py-1.5 text-xs font-medium rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer ${
                        selectedCategory === cat.slug
                          ? 'bg-primary-500 text-background-50'
                          : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                      }`}
                    >
                      {cat.name}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-4 gap-4 md:gap-5">
                {filteredComics.map((comic) => (
                  <ComicCard key={comic.id} comic={comic} />
                ))}
              </div>
            </div>
          </section>

          {/* Hot / Popular Comics */}
          <section className="w-full px-4 md:px-8 py-14 bg-background-100">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-end justify-between mb-8">
                <div>
                  <h2 className="font-heading text-2xl md:text-3xl text-foreground-950">
                    热门漫画推荐
                  </h2>
                  <p className="text-sm text-foreground-500 mt-1">
                    千万漫迷正在追的连载漫画，追漫必备精选
                  </p>
                </div>
                <Link
                  to="/category/rexue"
                  className="text-sm text-primary-500 hover:text-primary-600 font-medium transition-colors cursor-pointer whitespace-nowrap"
                >
                  查看更多 <i className="ri-arrow-right-line ml-1 text-xs"></i>
                </Link>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
                {hotComics.map((comic) => (
                  <ComicCard key={`hot-${comic.id}`} comic={comic} />
                ))}
              </div>
            </div>
          </section>

          {/* Category Browse */}
          <section className="w-full px-4 md:px-8 py-14 bg-background-50">
            <div className="max-w-7xl mx-auto">
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 text-center mb-2">
                按分类浏览漫画
              </h2>
              <p className="text-sm text-foreground-500 text-center mb-8">
                多种漫画分类，快速找到您感兴趣的连载漫画类型
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className="group flex flex-col items-center gap-3 p-5 rounded-lg bg-background-100 hover:bg-primary-50 border border-background-200/70 hover:border-primary-200 transition-all duration-200 cursor-pointer"
                  >
                    <div className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 group-hover:bg-primary-100 group-hover:text-primary-600 transition-colors">
                      <i className={`${cat.icon} text-xl`}></i>
                    </div>
                    <span className="text-sm font-medium text-foreground-700 group-hover:text-primary-600 transition-colors whitespace-nowrap">
                      {cat.name}
                    </span>
                    <span className="text-xs text-foreground-400">
                      {comics.filter((c) => c.category === cat.slug).length} 部漫画
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          </section>

          {/* Update Reminder CTA */}
          <section className="w-full px-4 md:px-8 py-14 bg-accent-500">
            <div className="max-w-3xl mx-auto text-center">
              <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-50/20 mb-5">
                <i className="ri-notification-3-line text-2xl text-background-50"></i>
              </div>
              <h2 className="font-heading text-2xl md:text-3xl text-background-50 mb-3">
                漫画更新提醒，追漫必备功能
              </h2>
              <p className="text-background-50/80 text-sm md:text-base mb-6">
                订阅更新提醒，第一时间获取最新漫画更新通知。免费漫画全集随时看，不再错过任何精彩章节！
              </p>
              <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row items-center justify-center gap-3 max-w-md mx-auto">
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="输入您的邮箱地址"
                  className="flex-1 w-full px-5 py-3 rounded-full text-sm bg-background-50 text-foreground-900 placeholder-foreground-400 focus:outline-none focus:ring-2 focus:ring-background-50/50 border-0"
                  required
                />
                <button
                  type="submit"
                  className="px-8 py-3 bg-foreground-950 text-background-50 font-medium rounded-full hover:bg-foreground-900 transition-all duration-200 cursor-pointer whitespace-nowrap text-sm"
                >
                  立即订阅
                </button>
              </form>
              {subscribeMsg && (
                <p className="mt-3 text-sm text-background-50/90 font-medium">{subscribeMsg}</p>
              )}
            </div>
          </section>

          {/* FAQ */}
          <section className="w-full px-4 md:px-8 py-14 bg-background-50">
            <div className="max-w-3xl mx-auto">
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 text-center mb-10">
                常见问题
              </h2>
              <div className="flex flex-col gap-3">
                {faqs.map((faq, idx) => (
                  <details key={idx} className="group rounded-lg bg-background-100 border border-background-200/70 overflow-hidden cursor-pointer">
                    <summary className="flex items-center justify-between px-5 py-4 text-sm font-medium text-foreground-800 list-none">
                      {faq.question}
                      <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform"></i>
                    </summary>
                    <div className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">
                      {faq.answer}
                    </div>
                  </details>
                ))}
              </div>
            </div>
          </section>

          {/* CTA Banner */}
          <section className="w-full px-4 md:px-8 py-14 bg-background-100">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-3">
                准备好开始追更了吗？
              </h2>
              <p className="text-sm text-foreground-500 mb-6">
                加入UU漫画，和千万漫迷一起追更最新漫画。免费漫画全集，更新提醒，从此追漫不再错过！
              </p>
              <Link
                to="/category/rexue"
                className="inline-flex items-center px-8 py-3 bg-primary-500 text-background-50 font-medium rounded-full hover:bg-primary-600 transition-all duration-200 cursor-pointer whitespace-nowrap text-sm"
              >
                立即开始追漫 <i className="ri-arrow-right-line ml-2"></i>
              </Link>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}